// src/services/updateService.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { startVersionPolling, applyVersionUpdate, type VersionInfo } from './versionChecker';

// Types
interface UpdateState {
  updateAvailable: boolean;
  updateInfo: {
    version?: string;
    changes?: string[];
    buildDate?: string;
    commit?: string;
  } | null;
  dismissedUpdates: string[];
}

// Initial state
const initialState: UpdateState = {
  updateAvailable: false,
  updateInfo: null,
  dismissedUpdates: []
};

// Redux slice for update management
export const updateSlice = createSlice({
  name: 'update',
  initialState,
  reducers: {
    setUpdateAvailable: (state, action: PayloadAction<boolean>) => {
      state.updateAvailable = action.payload;
    },
    setUpdateInfo: (state, action: PayloadAction<UpdateState['updateInfo']>) => {
      state.updateInfo = action.payload;
    },
    dismissUpdate: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload && state.updateInfo?.version) {
        state.dismissedUpdates.push(state.updateInfo.version);
      }
      state.updateAvailable = false;
    },
    applyUpdate: (state) => {
      state.updateAvailable = false;
      if (state.updateInfo?.version) {
        applyVersionUpdate(state.updateInfo.version);
      } else {
        window.location.reload();
      }
    }
  }
});

export const { setUpdateAvailable, setUpdateInfo, dismissUpdate, applyUpdate } = updateSlice.actions;
export default updateSlice.reducer;

// Singleton to prevent multiple initializations
let isInitialized = false;
let stopPolling: (() => void) | null = null;

// Init function to setup update detection
export const initUpdateDetection = (store: any) => {
  // Prevent multiple initializations
  if (isInitialized) return;
  isInitialized = true;

  // Check for vite HMR (development mode)
  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', (data: { updates: any[] }) => {
  //    // console.log('HMR update available:', data);
      store.dispatch(setUpdateAvailable(true));
      store.dispatch(setUpdateInfo({
        version: 'dev',
        changes: ['Development changes detected']
      }));
    });
  }

  // Start version polling (works in both dev and prod)
  stopPolling = startVersionPolling((versionInfo: VersionInfo) => {
    // New version detected
    store.dispatch(setUpdateAvailable(true));

    // Fetch changelog if available
    fetchChangelogInfo()
      .then(changelog => {
        store.dispatch(setUpdateInfo({
          version: versionInfo.version,
          buildDate: versionInfo.buildDate,
          commit: versionInfo.commit,
          changes: changelog?.changes || ['Nueva versión disponible']
        }));
      })
      .catch(() => {
        store.dispatch(setUpdateInfo({
          version: versionInfo.version,
          buildDate: versionInfo.buildDate,
          commit: versionInfo.commit,
          changes: ['Nueva versión disponible']
        }));
      });
  });

  // Check for service worker updates (production mode)
  if ('serviceWorker' in navigator) {
    let refreshing = false;

    // Only add the event listener once
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;

      // Get update information if available
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration?.waiting) {
          // We have an update ready to install
          store.dispatch(setUpdateAvailable(true));

          // Try to get version info from service worker
          fetchChangelogInfo()
            .then(info => {
              store.dispatch(setUpdateInfo(info));
            })
            .catch(() => {
              store.dispatch(setUpdateInfo({
                version: new Date().toISOString().split('T')[0],
                changes: ['Nueva versión disponible']
              }));
            });
        }
      });
    });
  }
};

// Helper function to fetch changelog info
const fetchChangelogInfo = async () => {
  try {
    const response = await fetch('/changelog.json?t=' + new Date().getTime());
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Could not fetch changelog');
  } catch (error) {
    console.error('Error fetching changelog:', error);
    return null;
  }
};

// Function to check for updates manually
export const checkForUpdates = async (store: any) => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();

    if (registration?.waiting) {
      // We have an update ready to install
      store.dispatch(setUpdateAvailable(true));

      // Try to get version info from service worker
      const info = await fetchChangelogInfo();
      store.dispatch(setUpdateInfo(info || {
        version: new Date().toISOString().split('T')[0],
        changes: ['Nueva versión disponible']
      }));

      return true;
    }

    // Check if there's a new version
    await registration?.update();
    return false;
  }

  return false;
};